import React from "react";
import { Button } from "primereact/button";
import localeText from "../../../../locale";
import { formatPrice } from "../../../../utilities/formatPrice";
import { Accordion, AccordionTab } from "primereact/accordion";
import GetUserInfoService from "../../../../service/GetUserInfoService";
import { useHistory } from "react-router-dom";
import { usePayment } from "../Checkout/context/PaymentContext";
import { useAuthDialog } from "../../../../contexts/AuthDialogContext/AuthDialogContext";
import useOrderCount from "../../../../hooks/useOrderCount";

const UnifiedOrderSummary = ({
  // Props comuns
  order,
  isFringeBenefitsPage,
  currencyProduct,
  currencyCodeUser,
  shippingQuotes,
  showMessageSellerResponsibility,
  amountShipmentForexUser,
  currencyUser,
  currencyFormat,
  walletExtracoinBalance,
  currencyExchangeValue,
  orderTotal,
  userPercentExtracoin,
  walletExtracoinBalanceForex,
  orderCurrency,
  walletBalance,
  amountToPayWithExtracredit,
  amountShipmentForexProduct,
  amountWallet,
  amountBankTransfer,
  cashback,
  remainingPrice,
  loading,
  setLoading,
  showErrorToast,
  page,
  setPage,

  // Props específicas do contexto
  context, // 'cart', 'checkout', 'payment'

  // Props específicas do Cart
  orderItems,
  isLoadingToContinue,
  setIsLoadingToContinue,
  sendInitiateCheckout,
  setPrice,
  orderKey,
  urlToCheckCatalog,

  hasCompleteAddress,
  isPickup,

  canProceed,
  validateCheckout,
  isServiceOrDownloadable,
  bankTransferReceipt,
  userInfo,
}) => {
  const history = useHistory();
  const getUserInfoService = new GetUserInfoService();
  const userInfoService = new GetUserInfoService();
  const { openLoginDialog } = useAuthDialog();
  const { orderCount } = useOrderCount();

  const hasRequiredUserInfo = () => {
    if (!userInfo) return false;
    return !!(
      userInfo.documentNumber &&
      userInfo.firstname &&
      userInfo.surname &&
      userInfo.phoneNumber
    );
  };

  const checkIsServiceOrDownloadable = () => {
    if (typeof isServiceOrDownloadable === "function") {
      return isServiceOrDownloadable();
    }
    return order?.itens?.some(
      (item) => item?.product?.service || item?.product?.downloadable
    );
  };

  const { sendMethod: contextSendMethod, orderTotal: contextOrderTotal } = usePayment();

  const computedSubtotal = (() => {
    if (isFringeBenefitsPage) {
      return Number(order?.totalOrder) || 0;
    }
    return Number(order?.amountOrder) || 0;
  })();

  const computeCheckoutOrPaymentTotal = () => {
    if ((context === "checkout" || context === "payment") && contextOrderTotal) {
      const total = parseFloat((Number(contextOrderTotal) || 0).toFixed(2));
      const wallet = parseFloat((Number(amountWallet) || 0).toFixed(2));
      const extra = parseFloat((Number(amountToPayWithExtracredit) || 0).toFixed(2));
      const bank = parseFloat((Number(amountBankTransfer) || 0).toFixed(2));

      const result = parseFloat((total - wallet - extra - bank).toFixed(2));
      return result;
    }

    const baseTotal = parseFloat(
      (isFringeBenefitsPage
        ? Number(order?.totalOrder) || 0
        : Number(order?.amountOrder) || 0
      ).toFixed(2)
    );

    const shipmentPrice = parseFloat((Number(order?.amountShipment) || 0).toFixed(2));
    const platformFee = parseFloat((Number(order?.amountToPlatform) || 0).toFixed(2));

    const total = parseFloat((baseTotal + shipmentPrice + platformFee).toFixed(2));
    const wallet = parseFloat((Number(amountWallet) || 0).toFixed(2));
    const extra = parseFloat((Number(amountToPayWithExtracredit) || 0).toFixed(2));
    const bank = parseFloat((Number(amountBankTransfer) || 0).toFixed(2));

    const result = parseFloat((total - wallet - extra - bank).toFixed(2));
    return result;
  };

  const computedTotal = (() => {
    if (context === "cart") {
      return isFringeBenefitsPage
        ? Number(order?.totalOrder) || 0
        : Number(order?.amountTotal) || Number(order?.amountOrder) || 0;
    }
    return computeCheckoutOrPaymentTotal();
  })();

  const showShippingRow = (() => {
    if (context === "cart") return false;
    if (isFringeBenefitsPage) return false;

    const isPhysical =
      typeof isServiceOrDownloadable === "function"
        ? !isServiceOrDownloadable()
        : !order?.itens?.[0]?.product?.downloadable && !order?.itens?.[0]?.product?.service;
    const hasShippingValue =
      (Number(order?.amountShipment) || Number(amountShipmentForexProduct) || 0) > 0;
    return isPhysical && (contextSendMethod || hasShippingValue);
  })();

  const shippingDisplayValue = () => {
    if (contextSendMethod?.currency && contextSendMethod?.currency !== currencyProduct) {
      return `${currencyFormat[currencyProduct]} ${formatPrice(
        Number(amountShipmentForexProduct) || 0
      )}`;
    }
    return `${currencyFormat[currencyProduct]} ${formatPrice(Number(order?.amountShipment) || 0)}`;
  };

  const platformFeeValue = !isFringeBenefitsPage ? Number(order?.amountToPlatform) || 0 : 0;
  const showPlatformFee = !isFringeBenefitsPage && platformFeeValue > 0;

  const handleCartFinishOrder = async () => {
    if (!localStorage.getItem("__extraconomy_token")) {
      openLoginDialog(true);
      return;
    }

    setLoading(true);
    setIsLoadingToContinue(true);

    const userInfo = await getUserInfoService.getUserInfo(
      localStorage.getItem("__extraconomy_token"),
      localStorage.getItem("__extraconomy_username")
    );

    if (userInfo.messageCode !== 200001) {
      showErrorToast({ code: userInfo.messageCode });
      setLoading(false);
      setIsLoadingToContinue(false);
      return;
    }

    if (orderCount === null) {
      showErrorToast({ code: 500000 });
      setLoading(false);
      setIsLoadingToContinue(false);
      return;
    }

    if (
      isFringeBenefitsPage
        ? order.fringeBenefit.amountAvailable === 0
        : order.itens.find(
            (product) => product.product.amountInStock === 0 && !product.product.downloadable
          )
    ) {
      showErrorToast({ code: 500074 });
      setLoading(false);
      setIsLoadingToContinue(false);
      return false;
    }

    await sendInitiateCheckout();

    const shouldSkipShippingPage = () => {
      if (isFringeBenefitsPage) return false;

      return order.itens.every((item) => item.product.downloadable || item.product.service);
    };

    const nextPage = shouldSkipShippingPage() ? 3 : page + 1;
    setPage(nextPage);
    setPrice(isFringeBenefitsPage ? order.totalOrder : order.amountTotal);
    setLoading(false);
    setIsLoadingToContinue(false);
  };

  const handleCheckoutFinishOrder = async () => {
    setLoading(true);

    const userInfo = await getUserInfoService.getUserInfo(
      localStorage.getItem("__extraconomy_token"),
      localStorage.getItem("__extraconomy_username")
    );

    if (userInfo.messageCode !== 200001) {
      showErrorToast({ code: userInfo.messageCode });
      setLoading(false);
      return;
    }

    if (orderCount === null) {
      showErrorToast({ code: 500000 });
      setLoading(false);
      return;
    }

    if (
      isFringeBenefitsPage
        ? order.fringeBenefit.amountAvailable === 0
        : order.itens.find(
            (product) => product.product.amountInStock === 0 && !product.product.downloadable
          )
    ) {
      showErrorToast({ code: 500074 });
      setLoading(false);
      return false;
    }

    setLoading(false);
    setPage(page + 1);
  };

  const renderButtons = () => {
    switch (context) {
      case "cart":
        return (
          <>
            <Button
              className="w-full border-none border-round-lg px-4 py-3 uppercase text-white font-semibold"
              label={localeText("finishOrder")}
              style={{ backgroundColor: "#28AB69" }}
              onClick={handleCartFinishOrder}
              loading={isLoadingToContinue || loading}
              disabled={!localStorage.getItem(orderKey) || isLoadingToContinue || loading}
            />
            <Button
              label={localeText("continueShopping")}
              onClick={() => history.push(urlToCheckCatalog)}
              className="w-full border-round-lg px-4 py-3 mt-2 font-semibold"
              style={{ backgroundColor: "#fff", color: "#28AB69", border: "1px solid #28AB69" }}
            />
          </>
        );

      case "checkout":
        return (
          <Button
            className="w-full border-none border-round-lg px-4 py-3 uppercase text-white font-semibold"
            label={localeText("goToPayment")}
            style={{ backgroundColor: "#28AB69" }}
            onClick={handleCheckoutFinishOrder}
            loading={loading}
            disabled={
              loading ||
              (!checkIsServiceOrDownloadable() && !hasRequiredUserInfo()) ||
              (!checkIsServiceOrDownloadable() && !hasCompleteAddress()) ||
              (order?.itens?.some(
                (item) =>
                  item?.product?.shippingOption === "PAID" ||
                  item?.product?.shippingOption === "PICKUP"
              ) &&
                !isPickup &&
                !contextSendMethod)
            }
          />
        );

      case "payment":
        return (
          <div className="flex mt-4">
            <Button
              className="w-full border-none border-round-lg px-4 py-3 uppercase text-white font-semibold"
              style={{ backgroundColor: "#28AB69" }}
              label={localeText("finish")}
              onClick={() => validateCheckout(true)}
              loading={loading}
              disabled={(!checkIsServiceOrDownloadable() && !hasRequiredUserInfo()) || !canProceed() || (amountBankTransfer && !bankTransferReceipt)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="col-12 md:col flex flex-column gap-2 sticky bottom-0 md:static payment-info-container"
      style={{ zIndex: 200 }}
    >
      <div
        className="p-4 border-round-lg border-none bg-white"
        style={{ boxShadow: "0px 0px 40px  #5F58521A" }}
      >
        <div className="flex justify-content-between align-items-baseline">
          <div className="font-bold uppercase" style={{ color: "#344563" }}>
            {localeText("orderResume")}
          </div>
          <span className="text-gray-500">
            {isFringeBenefitsPage ? 1 : orderItems?.length || order?.itens?.length}{" "}
            {(orderItems?.length || order?.itens?.length) > 1 ? "items" : "item"}
          </span>
        </div>
        <hr />

        <div className="flex justify-content-between mb-2">
          <span className="text-sm font-medium" style={{ color: "#505F79" }}>
            {localeText("subtotal")}
          </span>
          <span className="text-sm" style={{ color: "#091E42" }}>
            {currencyFormat[currencyProduct]} {formatPrice(computedSubtotal)}
          </span>
        </div>

        {showShippingRow && (
          <div className="flex justify-content-between mb-2">
            <span className="text-sm font-medium" style={{ color: "#505F79" }}>
              {localeText("shippingCost")}
            </span>
            <span className="text-sm" style={{ color: "#091E42" }}>
              {shippingDisplayValue()}
            </span>
          </div>
        )}
        {showPlatformFee && (
          <div className="flex justify-content-between ">
            <span className="text-sm font-medium" style={{ color: "#505F79" }}>
              {localeText("platformFee")}
            </span>
            <span className="text-sm" style={{ color: "#091E42" }}>
              {currencyFormat[currencyProduct]} {formatPrice(platformFeeValue)}
            </span>
          </div>
        )}

        {userInfo && walletExtracoinBalance >= 0 && (
          <>
            <div className="flex flex-column">
              <hr className="my-3 md:mb-0" />
              <span
                className="text-xs font-normal px-2 py-1 w-fit hidden md:inline-block"
                style={{ color: "#253858", backgroundColor: "#EBECF0" }}
              >
                {localeText("myCredits")}
              </span>
            </div>

            <Accordion
              activeIndex={-1}
              className="md:hidden flex flex-column gap-2 cart-accordion"
              headerClassName="flex justify-content-start"
            >
              <AccordionTab
                header={
                  <div className="flex w-full">
                    <span>{localeText("myCredits")}</span>
                  </div>
                }
              >
                <div className="flex justify-content-between">
                  <span>{localeText("balanceAvailable")}</span>
                  <span className="font-normal text-right" style={{ color: "#46A748" }}>
                    {currencyFormat[currencyProduct]}{" "}
                    {formatPrice(parseFloat(walletExtracoinBalance * (userPercentExtracoin / 100)))}
                    {currencyCodeUser !== orderCurrency && (
                      <>
                        {" "}
                        (≈ {currencyFormat[currencyProduct]}{" "}
                        {formatPrice(
                          parseFloat(walletExtracoinBalanceForex * (userPercentExtracoin / 100))
                        )}
                        )
                      </>
                    )}
                  </span>
                </div>
                <div className="flex justify-content-between">
                  <span>{localeText("walletBalance")}</span>
                  <span className="font-normal text-right" style={{ color: "#46A748" }}>
                    {currencyFormat[currencyProduct]} {formatPrice(walletBalance)}
                  </span>
                </div>
              </AccordionTab>
            </Accordion>

            <div className="hidden md:flex flex-column gap-2 mb-3 mt-2">
              <div className="flex justify-content-between">
                <span className="font-medium text-sm" style={{ color: "#505F79" }}>
                  {localeText("balanceAvailable")}
                </span>
                <span className="font-normal text-right" style={{ color: "#505F79" }}>
                  {currencyFormat[currencyProduct]}{" "}
                  {formatPrice(parseFloat(walletExtracoinBalance * (userPercentExtracoin / 100)))}
                  {currencyCodeUser !== orderCurrency && (
                    <>
                      {" "}
                      (≈ {currencyFormat[currencyProduct]}{" "}
                      {formatPrice(
                        parseFloat(walletExtracoinBalanceForex * (userPercentExtracoin / 100))
                      )}
                      )
                    </>
                  )}
                </span>
              </div>
              <div className="flex justify-content-between">
                <span className="font-medium text-sm" style={{ color: "#505F79" }}>
                  {localeText("walletBalance")}
                </span>
                <span className="font-normal text-right" style={{ color: "#505F79" }}>
                  {currencyFormat[currencyProduct]} {formatPrice(walletBalance)}
                </span>
              </div>
            </div>
          </>
        )}

        {(amountWallet > 0 || amountToPayWithExtracredit > 0 || amountBankTransfer > 0) && (
          <>
            <hr />
            {amountWallet > 0 && (
              <div className="flex justify-content-between">
                <span className="text-sm font-medium" style={{ color: "#505F79" }}>
                  {localeText("walletApplied")}
                </span>
                <span className="text-sm" style={{ color: "#505F79" }}>
                  -{currencyFormat[currencyProduct]} {formatPrice(amountWallet)}
                </span>
              </div>
            )}
            {amountToPayWithExtracredit > 0 && (
              <div className="flex justify-content-between">
                <span className="text-sm font-medium" style={{ color: "#505F79" }}>
                  {localeText("extracreditApplied")}
                </span>
                <span className="text-sm" style={{ color: "#505F79" }}>
                  -{currencyFormat[currencyProduct]} {formatPrice(amountToPayWithExtracredit)}
                </span>
              </div>
            )}
            {amountBankTransfer > 0 && (
              <div className="flex justify-content-between">
                <span className="text-sm font-medium" style={{ color: "#505F79" }}>
                  {localeText("bankTransferApplied")}
                </span>
                <span className="text-sm" style={{ color: "#505F79" }}>
                  -{currencyFormat[currencyProduct]} {formatPrice(amountBankTransfer)}
                </span>
              </div>
            )}
          </>
        )}

        <hr />
        {/* Final Total */}
        <div className="flex justify-content-between">
          <span className="text-sm font-medium" style={{ color: "#505F79" }}>
            {localeText("total")}
          </span>
          <span style={{ color: "#46A748", fontSize: "1rem" }} className="text-right">
            <b>
              {currencyFormat[currencyProduct]}{" "}
              {formatPrice((Number(remainingPrice) || 0) > 0 ? remainingPrice : computedTotal)}
            </b>
          </span>
        </div>

        <div className="text-right my-4 hidden md:block">
          <p className="text-sm font-light" style={{ color: "#7A869A" }}>
            {localeText("paypalPayment")}
          </p>
        </div>

        {cashback > 0 && (
          <div className="flex justify-content-center align-items-center gap-2 text-green-500 mb-3 mt-3 md:mt-0">
            <svg width={16} height={16} viewBox="0 0 17 17" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.377 1.26132C11.2474 1.13143 11.0365 1.12981 10.9056 1.25969L9.58789 2.56893L9.55367 2.60878C9.5125 2.66512 9.48958 2.73345 9.48958 2.80428C9.48958 2.8925 9.52409 2.97746 9.58659 3.04029L10.9355 4.39706L10.9879 4.44002C11.0433 4.47687 11.1074 4.49537 11.1719 4.49537C11.2572 4.49537 11.3418 4.46314 11.4069 4.39836C11.5378 4.26848 11.5378 4.05754 11.4082 3.92701L10.6313 3.14576L10.7782 3.15428C12.6785 3.30236 14.1803 4.89201 14.1803 6.82968C14.1803 7.01392 14.3294 7.16301 14.5137 7.16301C14.6979 7.16301 14.847 7.01392 14.847 6.82968C14.847 4.47225 12.9648 2.55099 10.6246 2.47876L11.3757 1.73267L11.4143 1.68669C11.5045 1.55723 11.4927 1.37764 11.377 1.26132ZM9.17969 4.66634C9.17969 2.73633 7.60938 1.16634 5.67969 1.16634C3.75 1.16634 2.17969 2.73633 2.17969 4.66634C2.17969 6.59635 3.75 8.16634 5.67969 8.16634C7.60938 8.16634 9.17969 6.59635 9.17969 4.66634ZM12.013 7.49967C13.9427 7.49967 15.513 9.06966 15.513 10.9997C15.513 12.9297 13.9427 14.4997 12.013 14.4997C10.0833 14.4997 8.51302 12.9297 8.51302 10.9997C8.51302 9.06966 10.0833 7.49967 12.013 7.49967ZM3.17969 8.49967C3.36393 8.49967 3.51302 8.64876 3.51302 8.83301C3.51302 10.7707 5.01485 12.3603 6.91515 12.5084L7.06209 12.5169L6.28516 11.7357C6.1556 11.6051 6.1556 11.3942 6.28646 11.2643C6.40278 11.1489 6.58185 11.1366 6.71166 11.2269L6.75781 11.2656L8.10677 12.6224C8.16927 12.6852 8.20378 12.7702 8.20378 12.8584C8.20378 12.9292 8.18086 12.9976 8.13969 13.0539L8.10547 13.0938L6.78776 14.403C6.72266 14.4674 6.63737 14.4997 6.55273 14.4997C6.4668 14.4997 6.38151 14.4668 6.31641 14.4014C6.20067 14.285 6.18883 14.1055 6.27908 13.976L6.31771 13.93L7.06877 13.1839C4.72856 13.1117 2.84635 11.1904 2.84635 8.83301C2.84635 8.64876 2.99544 8.49967 3.17969 8.49967ZM9.17969 10.9997C9.17969 9.4375 10.4505 8.16634 12.013 8.16634C13.5755 8.16634 14.8464 9.4375 14.8464 10.9997C14.8464 12.5618 13.5755 13.833 12.013 13.833C10.4505 13.833 9.17969 12.5618 9.17969 10.9997ZM12.0007 8.62565C12.1645 8.62565 12.3005 8.74345 12.3287 8.89903L12.334 8.95898V9.21745C12.4997 9.2531 12.6486 9.30497 12.7671 9.35406L12.8938 9.41043L13.0179 9.47404L13.0489 9.49186C13.2058 9.58691 13.2559 9.79102 13.1615 9.94824C13.077 10.0889 12.9061 10.1433 12.7585 10.0885L12.606 10.0113L12.4969 9.96283C12.351 9.90314 12.1449 9.83887 11.9349 9.83887C11.6726 9.83887 11.4011 9.95247 11.4011 10.1429C11.4011 10.3423 11.6262 10.4749 11.9862 10.6259L12.3693 10.7802L12.5462 10.8563C12.9577 11.0436 13.3425 11.3069 13.3425 11.807C13.3425 12.2126 13.1014 12.6411 12.4592 12.7798L12.334 12.8023V13.0404C12.334 13.2246 12.1849 13.3737 12.0007 13.3737C11.8369 13.3737 11.7009 13.2559 11.6727 13.1003L11.6674 13.0404V12.8026C11.2975 12.7517 11.049 12.6221 10.9096 12.5265L10.8327 12.469L10.7761 12.418C10.6478 12.2858 10.6517 12.0745 10.7839 11.9466C10.9025 11.8341 11.0819 11.8239 11.2099 11.9158L11.2727 11.9691L11.3225 12.0025C11.4223 12.0628 11.6367 12.1592 11.9896 12.1592C12.2461 12.1592 12.6758 12.1133 12.6758 11.807C12.6758 11.6749 12.5066 11.5673 12.243 11.451L12.054 11.3717L11.776 11.2606L11.5983 11.1845C11.1548 10.9845 10.7344 10.7008 10.7344 10.1429C10.7344 9.68617 11.0681 9.33211 11.5657 9.2149L11.6674 9.19466V8.95898C11.6674 8.77474 11.8165 8.62565 12.0007 8.62565ZM5.67969 1.83301C4.11719 1.83301 2.84635 3.10417 2.84635 4.66634C2.84635 6.22852 4.11719 7.49967 5.67969 7.49967C7.24219 7.49967 8.51302 6.22852 8.51302 4.66634C8.51302 3.10417 7.24219 1.83301 5.67969 1.83301ZM6.00521 4.9694C6.18945 4.9694 6.33854 5.11849 6.33854 5.30273C6.33854 5.46651 6.22074 5.6025 6.06517 5.6307L6.00521 5.63607H4.78296C5.01835 5.98783 5.35726 6.15723 5.82682 6.15723C6.21419 6.15723 6.40234 6.04492 6.71094 5.80664C6.85547 5.69401 7.06641 5.72005 7.17839 5.86719C7.29102 6.01302 7.26432 6.22233 7.11849 6.33464C6.77083 6.60221 6.43425 6.82389 5.82682 6.82389C5.03168 6.82389 4.43956 6.45623 4.09035 5.76197L4.03125 5.63607H3.70573C3.52148 5.63607 3.3724 5.48698 3.3724 5.30273C3.3724 5.13896 3.49019 5.00297 3.64577 4.97477L3.70573 4.9694H6.00521ZM7.11849 2.99805C6.77083 2.73047 6.43425 2.50879 5.82682 2.50879C4.91182 2.50879 4.26261 2.99158 3.94678 3.90365H3.70573L3.64577 3.90901C3.49019 3.93721 3.3724 4.07321 3.3724 4.23698C3.3724 4.42122 3.52148 4.57031 3.70573 4.57031H6.00521L6.06517 4.56495C6.22074 4.53675 6.33854 4.40075 6.33854 4.23698C6.33854 4.05273 6.18945 3.90365 6.00521 3.90365H4.67985L4.73208 3.80352C4.96719 3.38561 5.31731 3.17546 5.82682 3.17546C6.21419 3.17546 6.40234 3.28776 6.71094 3.52604C6.85677 3.63867 7.06706 3.6123 7.17839 3.46549C7.29102 3.31966 7.26432 3.11035 7.11849 2.99805Z"
                fill={"#28AB69"}
              />
            </svg>
            <span className="text-sm">Cashback extracredit</span>
            <span className="text-sm">
              <b>
                {currencyFormat[currencyProduct]} {formatPrice(cashback)}
              </b>
            </span>
          </div>
        )}

        {renderButtons()}
      </div>
    </div>
  );
};

export default UnifiedOrderSummary;
